import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { logException } from "@punchup/sentry";
import { bucketReferrer } from "./referrer-buckets";
import { isExcludedReferrer } from "./exclusions";
import { ComedianSourceCounts } from "../../models/comedian-weekly-digest";

const PAGE_SIZE = 10000;

/**
 * Folds "Link in bio" into "Your site / Other" for ticket buyers only.
 *
 * Per-product decision: the dedicated link-in-bio bucket was more
 * confusing than useful in the weekly digest, so it does not appear
 * in `ticket_source_breakdown`. Follow events keep their own bucket.
 *
 * Exported so the invariant can be regression-tested directly without
 * mocking the Supabase chain.
 */
export function consolidateTicketBucket(bucket: string): string {
  return bucket === "Link in bio" ? "Your site / Other" : bucket;
}

/**
 * Fetches ticket buyer events for a week range and returns per-comedian,
 * per-source-bucket sets of unique buyer distinct_ids.
 */
export async function fetchTicketBuyersBySource(
  supabase: SupabaseClient<Database>,
  weekStart: string,
  weekEnd: string
): Promise<ComedianSourceCounts> {
  const result: ComedianSourceCounts = new Map();
  let offset = 0;

  while (true) {
    const { data, error } = await supabase
      .from("events_tickets_merged")
      .select("*")
      .gte("timestamp", weekStart)
      .lt("timestamp", weekEnd)
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      logException(error, {
        data: "[comedian-weekly-digest] Failed to fetch ticket buyers",
      });
      throw error;
    }

    if (!data || data.length === 0) break;

    for (const row of data) {
      if (!row.ComedianId || !row.distinct_id) continue;
      if (
        isExcludedReferrer(
          row["$initial_referring_domain"] as string | null | undefined
        )
      )
        continue;

      const bucket = consolidateTicketBucket(
        bucketReferrer(
          row["$initial_referring_domain"],
          row["$initial_referrer"]
        )
      );

      let comedianMap = result.get(row.ComedianId);
      if (!comedianMap) {
        comedianMap = new Map();
        result.set(row.ComedianId, comedianMap);
      }

      let userSet = comedianMap.get(bucket);
      if (!userSet) {
        userSet = new Set();
        comedianMap.set(bucket, userSet);
      }

      userSet.add(row.distinct_id);
    }

    offset += data.length;
    if (data.length < PAGE_SIZE) break;
  }

  return result;
}
